import Component from "@glimmer/component";
import { service } from "@ember/service";
import { on } from "@ember/modifier";
import { task } from "ember-concurrency";
import taskHelper from "ember-concurrency/helpers/task";
import { t } from "ember-intl";
import { and, not, or } from "ember-truth-helpers";
import { saveRecord } from "@warp-drive/legacy/compat/builders";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntro from "glesys-controlpanel/components/page-content/service-intro";
import GButtonIcon from "glesys-controlpanel/components/g-button/icon";
import IconsPlus from "glesys-controlpanel/components/icons/plus";
import EmptyState from "glesys-controlpanel/components/empty-state";
import { cached } from "@glimmer/tracking";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import Entity from "glesys-controlpanel/components/entity";
import EntityField from "glesys-controlpanel/components/entity/fields/field";
import EntityDataCenterField from "glesys-controlpanel/components/entity/fields/data-center";
import EntityList from "glesys-controlpanel/components/entity-list";
import CostPlain from "glesys-controlpanel/components/cost/plain";
import { array } from "@ember/helper";
import IconsSidebarNetworking from "glesys-controlpanel/components/icons/sidebar/networking";

export default class NetworksOverview extends Component {
  @service store;

  get project() {
    return this.args.model.project;
  }

  get networks() {
    return this.args.model.networks;
  }

  @cached
  get filteredNetworks() {
    return this.networks
      .filter((network) => !network.isNew)
      .filter((network) => !network.isDeleted)
      .filter((network) => !network.isPublic);
  }

  createNetworkTask = task(async (description, dataCenter) => {
    let project = this.project;
    let network = this.store.createRecord("network", {
      project,
      dataCenter,
      description,
    });
    try {
      await this.store.request(saveRecord(network, { adapterOptions: { context: project.id } }));
    } catch (error) {
      network.unloadRecord();
      throw error;
    }
  });

  deleteNetworkTask = task(async (network) => {
    network.deleteRecord();
    try {
      await this.store.request(saveRecord(network, { adapterOptions: { context: this.project.id } }));
    } catch (error) {
      network.rollbackAttributes();
      throw error;
    }
  });

  <template>
    <PageContent>
      <PageContentServiceIntro @title={{t "networks.page-title"}}>
        {{#if (and (canManage) this.filteredNetworks)}}
          <GButtonIcon
            data-test-create-network
            @display="default"
            @color="green"
            {{on "click" (modal "modals/networks-create" task=this.createNetworkTask)}}
          >
            <IconsPlus class="w-4 -ml-1 mr-1" />
            {{t "button.create"}}
          </GButtonIcon>
        {{/if}}
      </PageContentServiceIntro>

      <EntityList @items={{this.filteredNetworks}}>
        <:default as |network|>
          <Entity
            @title={{or network.description network.id}}
            @key={{if network.description network.id}}
            @hideActions={{not (canManage)}}
          >
            <:fields>
              <EntityDataCenterField @dataCenter={{network.dataCenter}} />
              <EntityField data-test-network-cost @label={{t "label.cost"}}>
                <CostPlain @currency={{network.price.currency}} @price={{network.price.amount}} />
              </EntityField>
            </:fields>
            <:actions>
              <DropdownDestructiveItem
                data-test-network-delete
                {{on
                  "click"
                  (modal
                    "modals/organism-delete"
                    entity=network.id
                    entityKey=network.id
                    task=(taskHelper this.deleteNetworkTask network)
                  )
                }}
              >
                {{t "button.delete"}}
              </DropdownDestructiveItem>
            </:actions>
          </Entity>
        </:default>
        <:empty>
          <EmptyState
            @icon={{IconsSidebarNetworking}}
            @title={{t "networks.empty-state.title"}}
            @description={{t "networks.empty-state.description"}}
            @points={{array
              (t "networks.empty-state.usp-list.vmware-and-physical")
              (t "networks.empty-state.usp-list.off-public-internet")
              (t "networks.empty-state.usp-list.same-data-center")
            }}
            @learnMoreUrl="https://docs.glesys.com/products/compute/vmware-virtual-machines/how-tos/manage-vlan-networks"
            @learnMoreAriaLabel={{t "networks.empty-state.learn-more-aria-label"}}
            @createLabel={{t "networks.empty-state.button"}}
            @onCreate={{if (canManage) (modal "modals/networks-create" task=this.createNetworkTask)}}
          />
        </:empty>
      </EntityList>
    </PageContent>
  </template>
}
